import { I18n } from "@/i18n/I18n";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useProjectStore } from "@/frontend/store/ProjectStore";
import { useSampleMaturityStore } from "@/frontend/store/SampleMaturityStore";
import { useShallow } from "zustand/react/shallow";
import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { Button } from "react-bootstrap";
import { SampleMaturity } from "@/backend/models/sampleMaturity";
import { PencilSquare } from "@/frontend/SVGs/PencilSquare";
import { ButtonParent } from "../buttons/ButtonParent";
import { DeleteButton } from "../buttons/DeleteButton";
import { TruncatedText } from "../cards/TruncatedText";
import { SearchList } from "../SearchList";
import { NotFound } from "@/frontend/pages/NotFound";
import { LoadingSpinner } from "../LoadingSpinner";
import { NameDescriptionModal } from "../NameDescriptionModal";

export function ProjectSamplePhasesList() {
  const projectId = useParams().projectId;
  const project = useProjectStore(useShallow(state => state.projects.find(p => p.id === projectId)));
  const userClass = useGlobalStore(state => state.userClass);

  const sampleMaturities = useSampleMaturityStore(state => state.sampleMaturities);
  const fetchSampleMaturitiesByProjectId = useSampleMaturityStore(state => state.fetchSampleMaturitiesByProjectId);
  const createSampleMaturity = useSampleMaturityStore(state => state.createSampleMaturity);
  const resetSampleMaturityStore = useSampleMaturityStore(state => state.reset);

  const [sampleMaturityList, setSampleMaturityList] = useState<SampleMaturity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (projectId) {
      setLoading(true);
      void fetchSampleMaturitiesByProjectId(projectId).finally(() => {
        setLoading(false);
      });
    }

    return () => {
      resetSampleMaturityStore();
    };
  }, [projectId]);

  useEffect(() => {
    setSampleMaturityList(sampleMaturities);
  }, [sampleMaturities]);

  if (!projectId) {
    return <NotFound />;
  };

  const canManageSamplePhases = userClass?.getOrgId() === project?.propelOrgId;

  return (
    <div className="p-3">
      <div className="pb-3">
        <div className="d-flex gap-2 align-items-center">
          <h1 className="border-end pe-2">{I18n.get('samplePhases')}</h1>
          { canManageSamplePhases &&
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              {I18n.get('createModel', { model: I18n.get('samplePhaseUppercase') })}
            </Button>
          }
        </div>
      </div>
      <div>
          <SearchList
            items={sampleMaturities}
            setItems={setSampleMaturityList}
            modelLabel={I18n.get('samplePhaseUppercase')}
          />
          <div className="mt-3">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>{I18n.get('name')}</th>
                  <th>{I18n.get('description')}</th>
                  {canManageSamplePhases && <th className="text-end">{I18n.get('actions')}</th>}
                </tr>
              </thead>
              <tbody>
                <SamplePhasesTable sampleMaturities={sampleMaturityList} canManageSamplePhases={canManageSamplePhases} loading={loading} />
              </tbody>
            </table>
          </div>
        </div>
        {showCreateModal && <NameDescriptionModal
          onHide={() => setShowCreateModal(false)}
          title={I18n.get('createModel', { model: I18n.get('samplePhaseUppercase') })}
          onConfirm={(data) => createSampleMaturity({ ...data, projectId })}
        />}
    </div>
  );
}

function SamplePhasesTable(props: { sampleMaturities: SampleMaturity[], canManageSamplePhases: boolean, loading: boolean }) {
  const { sampleMaturities, canManageSamplePhases, loading } = props;

  if(loading) {
    return (
      <tr>
        <td colSpan={3} className="text-center py-4">
          <LoadingSpinner />
        </td>
      </tr>
    );
  }

  if(sampleMaturities.length === 0) {
    return (
      <tr>
        <td colSpan={3} className="text-center py-4">
          <p className="text-muted">{I18n.get('noSamplePhases')}</p>
        </td>
      </tr>
    );
  }

  return sampleMaturities.map((sampleMaturity) => (
    <SamplePhaseRow key={sampleMaturity.id} sampleMaturity={sampleMaturity} canManageSamplePhases={canManageSamplePhases} />
  ));
}

function SamplePhaseRow(props: { sampleMaturity: SampleMaturity, canManageSamplePhases: boolean }) {
  const { sampleMaturity, canManageSamplePhases } = props;

  const deleteSampleMaturity = useSampleMaturityStore(state => state.deleteSampleMaturity);
  const updateSampleMaturity = useSampleMaturityStore(state => state.updateSampleMaturity);

  const [showEditModal, setShowEditModal] = useState(false);

  return (
    <tr>
      <td>{sampleMaturity.name}</td>
      <td><TruncatedText text={sampleMaturity.description ?? ''} maxLength={100} showButton={false} /></td>
      {canManageSamplePhases && (
        <td className="text-end">
          <div className="d-flex justify-content-end gap-2">
            <ButtonParent
              variant="secondary"
              onClick={() => setShowEditModal(true)}
              title={I18n.get('editModel', { model: I18n.get('samplePhaseUppercase'), title: sampleMaturity.name })}
            >
              <PencilSquare />
            </ButtonParent>
            <DeleteButton
              model={I18n.get('samplePhaseUppercase')}
              title={sampleMaturity.name}
              onDelete={() => deleteSampleMaturity(sampleMaturity.id)}
            />
          </div>
        </td>
      )}
      {showEditModal && 
        <NameDescriptionModal
          onHide={() => setShowEditModal(false)}
          title={I18n.get('editModel', { model: I18n.get('samplePhaseUppercase'), title: sampleMaturity.name })}
          onConfirm={(data) => updateSampleMaturity(sampleMaturity.id, data)}
          currentName={sampleMaturity.name}
          currentDescription={sampleMaturity.description}
        />
      }
    </tr>
  );
};
